import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { InvitationCollectionActionTaken } from '@app/v2/event/enums/invitation-collection.enum'
import { InvitationChannelPricingType } from '@app/v2/event/types/invitation-channel-pricing.type'
import { InvitationChannelHistoryState } from '@app/v2/event/types/invitation-tracker.type'
import {
  INVITATION_CHANNEL_BLOCKED_SEND_ICON_SRC,
  INVITATION_CHANNEL_POPOVER_HEADER_ICON_SRC,
  invitationChannelBlockedSendBannerClassDark,
  invitationChannelSendCtaLabel,
  InvitationChannelSendRequest,
  invitationChannelTitle,
  invitationChannelUnitPrice,
  invitationChannelUnitPriceLabel,
  invitationHistoryTotalCostLabel
} from '@app/v2/event/utils/invitation-channel-ui.util'
import { resolveInvitationChannelBlockedSendState } from '@app/v2/event/utils/invitation-channel-blocked-send.util'
import { getActiveInvitationChannelHistory, InvitationChannel } from '@app/v2/event/utils/invitation-channel-status.util'
import { NgxTippyModule, type NgxTippyProps } from 'ngx-tippy-wrapper'
import type { Instance as TippyInstance } from 'tippy.js'
import { InvitationChannelHistoryBodyComponent } from '../invitation-channel-history-body/invitation-channel-history-body.component'

@Component({
  selector: 'app-invitation-channel-popover',
  standalone: true,
  imports: [NgClass, NgxTippyModule, InvitationChannelHistoryBodyComponent],
  templateUrl: './invitation-channel-popover.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tw-inline-flex tw-shrink-0'
  }
})
export class InvitationChannelPopoverComponent {
  public readonly collectionItemId = input.required<number>()
  public readonly channel = input.required<InvitationChannel>()
  public readonly history = input<InvitationChannelHistoryState | null>(null)
  public readonly pricingKind = input<InvitationChannelPricingType>('invitation')
  public readonly isSending = input(false)
  public readonly actionTaken = input<InvitationCollectionActionTaken | null>(null)
  public readonly inviteeName = input('')
  public readonly channelLatestStatus = input<string | null>(null)
  public readonly isOptOut = input(false)
  public readonly isEventFinished = input(false)
  public readonly sendInvitation = output<InvitationChannelSendRequest>()
  public readonly opened = output<InvitationChannelSendRequest>()
  public readonly loadMoreHistory = output<void>()

  public readonly activeHistory = computed(() => getActiveInvitationChannelHistory(this.history(), this.collectionItemId(), this.channel()))

  public readonly invitationCount = computed(() => this.activeHistory()?.total ?? 0)

  public readonly showTotalCost = computed(() => {
    const history = this.activeHistory()
    return !!history && !history.isLoading && history.total > 0
  })

  public readonly headerTitle = computed(() => invitationChannelTitle(this.channel(), this.invitationCount()))

  public readonly headerIconSrc = computed(() => INVITATION_CHANNEL_POPOVER_HEADER_ICON_SRC[this.channel()])

  public readonly totalCostLabel = computed(() => invitationHistoryTotalCostLabel(this.activeHistory()?.totalCost ?? 0))

  public readonly unitPriceLabel = computed(() =>
    invitationChannelUnitPriceLabel(
      invitationChannelUnitPrice(this.channel(), this.pricingKind(), this._authService.invitationUnitPriceUsd, this._authService.resendConfirmationUnitPriceUsd)
    )
  )

  public readonly sendCtaLabel = computed(() => (this.isSending() ? 'Sending…' : invitationChannelSendCtaLabel(this.unitPriceLabel())))

  public readonly blockedSendState = computed(() =>
    resolveInvitationChannelBlockedSendState({
      actionTaken: this.actionTaken(),
      channel: this.channel(),
      historyLatestStatus: this.activeHistory()?.entries?.[0]?.status,
      channelLatestStatus: this.channelLatestStatus(),
      inviteeName: this.inviteeName(),
      isOptOut: this.isOptOut(),
      isEventFinished: this.isEventFinished()
    })
  )

  public readonly blockedSendReason = computed(() => this.blockedSendState().reason)

  public readonly showSendCta = computed(() => this.blockedSendState().showSendCta)

  public readonly blockedSendMessage = computed(() => this.blockedSendState().message)

  public readonly blockedSendIconSrc = computed(() => {
    const reason = this.blockedSendReason()
    return reason ? INVITATION_CHANNEL_BLOCKED_SEND_ICON_SRC[reason] : ''
  })

  public readonly blockedSendBannerClass = computed(() => {
    const reason = this.blockedSendReason()
    return reason ? invitationChannelBlockedSendBannerClassDark(reason) : ''
  })

  public readonly tippyProps: NgxTippyProps = {
    theme: 'invitation-channel',
    trigger: 'click',
    interactive: true,
    placement: 'bottom',
    arrow: true,
    offset: [0, 8],
    maxWidth: 'none',
    appendTo: () => document.body,
    zIndex: 1400,
    hideOnClick: true,
    onShow: () => {
      this.opened.emit({
        collectionItemId: this.collectionItemId(),
        channel: this.channel()
      })
    },
    onMount: (instance) => {
      this._bindOpenListeners(instance)
    },
    onHidden: (instance) => {
      this._unbindOpenListeners(instance)
    },
    onDestroy: (instance) => {
      this._unbindOpenListeners(instance)
    }
  }

  private readonly _authService = inject(AuthService)
  private readonly _unbindOpenListenersByInstance = new WeakMap<TippyInstance, () => void>()

  /**
   * Forwards the send CTA click with collection item id + channel.
   */
  public onSendInvitation(): void {
    if (this.isSending() || !this.showSendCta()) return
    this.sendInvitation.emit({
      collectionItemId: this.collectionItemId(),
      channel: this.channel()
    })
  }

  /**
   * Binds listeners that live while the popover is open:
   * - hides the popover when the page/table scrolls (but not when scrolling inside the tippy content)
   * - re-runs popper positioning (incl. flip) when content size changes, e.g. once history loads
   * @param instance - The tippy instance to bind the listeners for.
   */
  private _bindOpenListeners(instance: TippyInstance): void {
    this._unbindOpenListeners(instance)

    const onScroll: EventListener = (event) => {
      const target = event.target
      if (target instanceof Node && instance.popper.contains(target)) return
      instance.hide()
    }
    const resizeObserver = new ResizeObserver(() => instance.popperInstance?.update())

    window.addEventListener('scroll', onScroll, true)
    resizeObserver.observe(instance.popper)

    this._unbindOpenListenersByInstance.set(instance, () => {
      window.removeEventListener('scroll', onScroll, true)
      resizeObserver.disconnect()
    })
  }

  /**
   * Removes the open-state listeners for a tippy instance.
   */
  private _unbindOpenListeners(instance: TippyInstance): void {
    this._unbindOpenListenersByInstance.get(instance)?.()
    this._unbindOpenListenersByInstance.delete(instance)
  }
}
